import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  doc,
  updateDoc,
  writeBatch,
  onSnapshot,
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from './firebase';
import logger from '../utils/logger';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRecoverableFirestoreWriteError = (error) => {
  const msg = String(error?.message || '');
  return (
    ['unavailable', 'deadline-exceeded', 'aborted', 'cancelled'].includes(
      error?.code
    ) || msg.includes('INTERNAL ASSERTION FAILED')
  );
};

const logNotificationWriteError = (message, error) => {
  if (isRecoverableFirestoreWriteError(error)) {
    logger.warn(message, error);
    return;
  }
  logger.error(message, error);
};

async function persistNotificationsReadState(notificationIds) {
  const writeAll = () =>
    Promise.allSettled(
      notificationIds.map((id) =>
        updateDoc(doc(db, 'notifications', id), { isRead: true })
      )
    );

  let results = await writeAll();
  let failures = results
    .filter((result) => result.status === 'rejected')
    .map((result) => result.reason);

  if (failures.length && failures.every(isRecoverableFirestoreWriteError)) {
    await sleep(400);
    results = await writeAll();
    failures = results
      .filter((result) => result.status === 'rejected')
      .map((result) => result.reason);
  }

  if (failures.length) {
    throw failures[0];
  }
}

const NotificationsContext = createContext();

export const useNotifications = () => {
  return useContext(NotificationsContext);
};

// Function to create a favicon with badge
const createFaviconWithBadge = (hasUnread) => {
  try {
    const favicon = document.querySelector('link[rel="icon"]');
    if (!hasUnread) {
      if (favicon) favicon.href = '/favicon.ico';
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 32;
    canvas.height = 32;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      ctx.clearRect(0, 0, 32, 32);
      ctx.drawImage(img, 0, 0, 32, 32);

      // Draw smaller red badge (no number)
      ctx.fillStyle = '#ff4444';
      ctx.beginPath();
      ctx.arc(24, 8, 7, 0, 2 * Math.PI); // radius set to 7
      ctx.fill();

      if (favicon) favicon.href = canvas.toDataURL();
    };
    img.onerror = () => {
      if (favicon) favicon.href = '/favicon.ico';
    };
    img.src = '/favicon.ico';
  } catch (error) {
    logger.warn('Error creating favicon badge:', error);
  }
};

// Helper to remove any leading (1) from the title
const cleanTitle = (title) => title.replace(/^\(\d+\)\s*/, '');

// Function to update favicon badge
const updateFaviconBadge = (hasUnread) => {
  try {
    // Check if the Favicon API is supported (limited browser support)
    if ('setAppBadge' in navigator && 'clearAppBadge' in navigator) {
      if (hasUnread) {
        navigator.setAppBadge(1);
      } else {
        navigator.clearAppBadge();
      }
    }

    // Create dynamic favicon with badge (works in all browsers)
    createFaviconWithBadge(hasUnread);

    // Always clean the title first
    const baseTitle = cleanTitle(document.title);

    if (hasUnread) {
      document.title = `(1) ${baseTitle}`;
    } else {
      document.title = baseTitle;
    }
  } catch (error) {
    logger.warn('Error updating favicon badge:', error);
  }
};

export const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [lastNotification, setLastNotification] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = getAuth();

  const fetchNotifications = useCallback(
    async (user, lastDoc = null) => {
      if (!user) return { notifications: [], lastDoc: null, hasMore: false };

      try {
        logger.debug('Fetching notifications for user:', user.uid);

        let notificationsQuery = query(
          collection(db, 'notifications'),
          where('recipientId', '==', user.uid),
          orderBy('timestamp', 'desc'),
          limit(30)
        );

        if (lastDoc) {
          notificationsQuery = query(notificationsQuery, startAfter(lastDoc));
        }

        logger.debug('Executing notifications query...');
        const querySnapshot = await getDocs(notificationsQuery);

        const notificationsList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        logger.debug(`Fetched ${notificationsList.length} notifications`);

        return {
          notifications: notificationsList,
          lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1],
          hasMore: querySnapshot.docs.length === 30,
        };
      } catch (error) {
        logger.error('Error fetching notifications:', error);
        // Check if it's a permissions error
        if (error.code === 'permission-denied') {
          setError('Permission denied. Please try logging in again.');
        } else {
          setError('Failed to load notifications. Please try again later.');
        }
        return { notifications: [], lastDoc: null, hasMore: false };
      }
    },
    []
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        setNotifications([]);
        setLastNotification(null);
        setHasMore(false);
        setLoading(false);
        // Clear badge when user logs out
        updateFaviconBadge(false);
      }
    });

    return () => unsubscribe();
  }, [auth]);

  // Single useEffect for handling notifications
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    logger.debug('Setting up notifications listener for:', currentUser.uid);
    setLoading(true);

    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('recipientId', '==', currentUser.uid),
      orderBy('timestamp', 'desc'),
      limit(30)
    );

    const unsubscribe = onSnapshot(notificationsQuery, {
      next: (snapshot) => {
        // Do not skip snapshots with hasPendingWrites — that can delay or mask
        // server updates when local reads/writes overlap the same listener.

        const notificationsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        logger.debug(`Received ${notificationsList.length} notifications`);
        setNotifications(notificationsList);
        setLastNotification(snapshot.docs[snapshot.docs.length - 1]);
        setHasMore(snapshot.docs.length === 30);
        setLoading(false);
        setError(null);
      },
      error: (error) => {
        logger.error('Notifications listener error:', error);
        setError(error.message);
        setLoading(false);
      },
    });

    return () => {
      logger.debug('Cleaning up notifications listener');
      unsubscribe();
    };
  }, [currentUser]);

  const fetchMoreNotifications = useCallback(async () => {
    if (!hasMore || !lastNotification || !currentUser) return;

    const {
      notifications: newNotifications,
      lastDoc,
      hasMore: moreAvailable,
    } = await fetchNotifications(currentUser, lastNotification);

    setNotifications((prevNotifications) => [
      ...prevNotifications,
      ...newNotifications,
    ]);
    setLastNotification(lastDoc);
    setHasMore(moreAvailable);
  }, [hasMore, lastNotification, currentUser, fetchNotifications]);

  const markAllAsRead = useCallback(async () => {
    if (!currentUser) return;

    const unreadIds = notifications
      .filter((notification) => !notification.isRead)
      .map((notification) => notification.id);

    if (unreadIds.length === 0) return;

    setNotifications((prevNotifications) =>
      prevNotifications.map((notification) => ({
        ...notification,
        isRead: true,
      }))
    );

    try {
      const batch = writeBatch(db);
      unreadIds.forEach((notificationId) => {
        batch.update(doc(db, 'notifications', notificationId), { isRead: true });
      });
      await batch.commit();
    } catch (error) {
      try {
        await persistNotificationsReadState(unreadIds);
      } catch (retryError) {
        logNotificationWriteError(
          'Error marking all notifications as read:',
          retryError
        );
        if (!isRecoverableFirestoreWriteError(retryError)) {
          setError(retryError.message);
        }
      }
    }
  }, [currentUser, notifications]);

  const markNotificationAsRead = useCallback(
    (notificationId) => {
      if (!currentUser || !notificationId) return;

      const isAlreadyRead = notifications.some(
        (notification) => notification.id === notificationId && notification.isRead
      );
      if (isAlreadyRead) return;

      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          notification.id === notificationId
            ? { ...notification, isRead: true }
            : notification
        )
      );

      persistNotificationsReadState([notificationId]).catch((error) => {
        logNotificationWriteError('Error marking notification as read:', error);
        if (!isRecoverableFirestoreWriteError(error)) {
          setError(error.message);
        }
      });
    },
    [currentUser, notifications]
  );

  const markNotificationsAsRead = useCallback(
    (notificationIds) => {
      if (!currentUser || !notificationIds?.length) return;

      const unreadIds = [...new Set(notificationIds.filter(Boolean))].filter(
        (id) => notifications.some((notification) => notification.id === id && !notification.isRead)
      );
      if (!unreadIds.length) return;

      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          unreadIds.includes(notification.id)
            ? { ...notification, isRead: true }
            : notification
        )
      );

      persistNotificationsReadState(unreadIds).catch((error) => {
        logNotificationWriteError(
          'Error marking grouped notifications as read:',
          error
        );
        if (!isRecoverableFirestoreWriteError(error)) {
          setError(error.message);
        }
      });
    },
    [currentUser, notifications]
  );

  const hasUnreadNotifications = useMemo(() => {
    return notifications.some((notification) => !notification.isRead);
  }, [notifications]);

  // Update favicon badge when unread notifications change
  useEffect(() => {
    updateFaviconBadge(hasUnreadNotifications);
  }, [hasUnreadNotifications]);

  const value = useMemo(
    () => ({
      notifications,
      hasUnreadNotifications,
      hasMore,
      markAllAsRead,
      markNotificationAsRead,
      markNotificationsAsRead,
      fetchMoreNotifications,
      loading,
      error,
    }),
    [
      notifications,
      hasUnreadNotifications,
      hasMore,
      markAllAsRead,
      markNotificationAsRead,
      markNotificationsAsRead,
      fetchMoreNotifications,
      loading,
      error,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export { createFaviconWithBadge };
